import { useForm } from "@inertiajs/react"
import { ImageIcon, MessageSquare, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"

export interface AnnouncementFormValues {
  content: string
  status: "published" | "draft"
  images: File[]
  videos: File[]
  neighborhood_id?: number | string | null
  images_to_delete?: (number | string)[]
  videos_to_delete?: (number | string)[]
}

export interface AnnouncementInitial {
  id?: number
  content?: string
  content_text?: string
  status?: "published" | "draft"
  images?: { id: number | string; url: string }[]
  video_url?: string | null
  videos?: { id: number | string; url: string }[]
  neighborhood?: { id: number; name: string } | null
}

interface Props {
  initial?: AnnouncementInitial
  onSuccessRedirect?: string | null
  submitPath: string
  method: "post" | "put"
  title?: string
}

interface ExistingMedia {
  id: number | string
  url: string
}

export default function AnnouncementForm({
  initial,
  onSuccessRedirect = null,
  submitPath,
  method,
  title,
}: Props) {
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const videoInputRef = useRef<HTMLInputElement | null>(null)

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { data, setData, post, processing, errors, reset, transform } = useForm(
    {
      content: initial?.content_text ?? initial?.content ?? "",
      status: initial?.status ?? "published",
      images: [] as File[],
      videos: [] as File[],
      neighborhood_id: initial?.neighborhood?.id ?? null,
      images_to_delete: [] as (number | string)[],
      videos_to_delete: [] as (number | string)[],
    },
  )

  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [videoPreviews, setVideoPreviews] = useState<string[]>([])

  useEffect(() => {
    if (data.images?.length) {
      const urls = data.images.map((f: File) => URL.createObjectURL(f))
      setImagePreviews(urls)
      return () => urls.forEach((u: string) => URL.revokeObjectURL(u))
    } else {
      setImagePreviews([])
    }
  }, [data.images])

  useEffect(() => {
    if (data.videos?.length) {
      const urls = data.videos.map((f: File) => URL.createObjectURL(f))
      setVideoPreviews(urls)
      return () => urls.forEach((u: string) => URL.revokeObjectURL(u))
    } else {
      setVideoPreviews([])
    }
  }, [data.videos])

  const [existingImages, setExistingImages] = useState<ExistingMedia[]>(
    initial?.images ?? [],
  )
  const [imagesMarkedForDeletion, setImagesMarkedForDeletion] = useState<
    Set<number | string>
  >(new Set())
  const [existingVideos, setExistingVideos] = useState<ExistingMedia[]>(
    initial?.videos ?? [],
  )
  const [videosMarkedForDeletion, setVideosMarkedForDeletion] = useState<
    Set<number | string>
  >(new Set())
  useEffect(() => {
    setExistingImages(initial?.images ?? [])
    setExistingVideos(initial?.videos ?? [])
  }, [initial?.id])

  const removeNewImageAt = (index: number) => {
    if (!Array.isArray(data.images)) return
    const next = data.images.filter((_: File, i: number) => i !== index)
    setData("images", next)
    if (next.length === 0 && imageInputRef.current) {
      imageInputRef.current.value = ""
    }
  }

  const removeNewVideoAt = (index: number) => {
    if (!Array.isArray(data.videos)) return
    const next = data.videos.filter((_: File, i: number) => i !== index)
    setData("videos", next)
    if (next.length === 0 && videoInputRef.current) {
      videoInputRef.current.value = ""
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const options = {
      forceFormData: true,
      onSuccess: () => {
        if (onSuccessRedirect) window.location.href = onSuccessRedirect
        reset("images", "videos")
        if (imageInputRef.current) imageInputRef.current.value = ""
        if (videoInputRef.current) videoInputRef.current.value = ""
      },
    } as const

    if (method === "post") {
      post(submitPath, options)
    } else {
      transform((d: Record<string, unknown>) => ({ ...d, _method: "put" }))
      post(submitPath, options)
      transform((d: Record<string, unknown>) => d)
    }
  }

  const toggleMarkExistingImage = (attachmentId: number | string) => {
    const s = new Set(imagesMarkedForDeletion)
    if (s.has(attachmentId)) {
      s.delete(attachmentId)
    } else {
      s.add(attachmentId)
    }
    setImagesMarkedForDeletion(s)
    setData("images_to_delete", Array.from(s))
  }
  const toggleMarkExistingVideo = (attachmentId: number | string) => {
    const s = new Set(videosMarkedForDeletion)
    if (s.has(attachmentId)) {
      s.delete(attachmentId)
    } else {
      s.add(attachmentId)
    }
    setVideosMarkedForDeletion(s)
    setData("videos_to_delete", Array.from(s))
  }

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <MessageSquare className="text-primary h-8 w-8" />
        {title ?? (method === "post" ? "Crear anuncio" : "Editar anuncio")}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(errors).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(errors).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}
        <form
          onSubmit={onSubmit}
          encType="multipart/form-data"
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="content">Contenido</Label>
            <Textarea
              id="content"
              rows={6}
              value={data.content}
              onChange={(e) => setData("content", e.target.value)}
              placeholder=""
              className="border-border focus:border-primary resize-none border-2 text-base transition-colors duration-200"
            />
            {errors.content ? (
              <p className="text-sm text-red-600">{String(errors.content)}</p>
            ) : null}
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="mb-2 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="text-foreground flex items-center gap-2 text-lg font-semibold">
                <ImageIcon className="text-primary h-5 w-5" />
                Medios
              </h3>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="images">Imágenes</Label>
              <Input
                id="images"
                ref={imageInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? [])
                  if (files.length === 0) return
                  const merged = [...(data.images ?? []), ...files]
                  setData("images", merged)
                  if (imageInputRef.current) imageInputRef.current.value = ""
                }}
                aria-label="Subir imágenes"
                className="border-muted file:bg-primary/10 file:text-primary hover:file:bg-primary/20 h-12 border-2 border-dashed file:mr-4 file:truncate file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
              />
              {errors.images ? (
                <p className="text-sm text-red-600">{String(errors.images)}</p>
              ) : null}

              {(existingImages.length > 0 || imagePreviews.length > 0) && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {existingImages.map((img) => {
                    const isMarked = imagesMarkedForDeletion.has(img.id)
                    if (isMarked) return null
                    return (
                      <div key={img.id as string} className="relative">
                        <img
                          src={img.url}
                          alt="imagen"
                          className="bg-muted aspect-square rounded border object-cover opacity-100"
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => toggleMarkExistingImage(img.id)}
                          aria-label="Marcar imagen para eliminar"
                          className="bg-background/80 hover:bg-background absolute top-1 right-1 z-10 h-7 w-7 rounded-full shadow"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                  {imagePreviews.map((url, i) => (
                    <div key={`p-${i}`} className="relative">
                      <img
                        src={url}
                        alt="preview"
                        className="bg-muted aspect-square rounded border object-cover"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeNewImageAt(i)}
                        aria-label="Quitar imagen"
                        className="bg-background/80 hover:bg-background absolute top-1 right-1 z-10 h-7 w-7 rounded-full shadow"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="videos">Videos</Label>
              <Input
                id="videos"
                ref={videoInputRef}
                type="file"
                multiple
                accept="video/*"
                onChange={(e) =>
                  setData("videos", Array.from(e.target.files ?? []))
                }
                aria-label="Subir video"
                className="border-muted file:bg-primary/10 file:text-primary hover:file:bg-primary/20 h-12 border-2 border-dashed file:mr-4 file:truncate file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
              />
              {errors.videos ? (
                <p className="text-sm text-red-600">{String(errors.videos)}</p>
              ) : null}

              {(existingVideos.length > 0 || videoPreviews.length > 0) && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {existingVideos.map((v) => {
                    const isMarked = videosMarkedForDeletion.has(v.id)
                    if (isMarked) return null
                    return (
                      <div key={v.id as string} className="relative">
                        <video
                          className="w-full rounded border bg-black"
                          controls
                          src={v.url}
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => toggleMarkExistingVideo(v.id)}
                          aria-label="Marcar video para eliminar"
                          className="bg-background/80 hover:bg-background absolute top-2 right-2 z-10 h-8 w-8 rounded-full shadow"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                  {videoPreviews.map((url, i) => (
                    <div key={`vp-${i}`} className="relative">
                      <video
                        className="w-full rounded border bg-black"
                        controls
                        src={url}
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeNewVideoAt(i)}
                        aria-label="Quitar video"
                        className="bg-background/80 hover:bg-background absolute top-2 right-2 z-10 h-8 w-8 rounded-full shadow"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Separator />

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="status">Estado</Label>
              <Select
                value={data.status}
                onValueChange={(val) =>
                  setData("status", val as "published" | "draft")
                }
              >
                <SelectTrigger
                  id="status"
                  className="border-border focus:border-primary h-12 w-full border-2 px-3 py-5 transition-colors duration-200"
                >
                  <SelectValue placeholder="Selecciona estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Publicado</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                </SelectContent>
              </Select>
              {errors.status ? (
                <p className="text-sm text-red-600">{String(errors.status)}</p>
              ) : null}
            </div>
          </div>
          <div className="border-border flex w-full justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              asChild
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              <a
                href={
                  method === "put" && initial?.id
                    ? `/anuncios/${initial.id}`
                    : "/feed"
                }
              >
                Cancelar
              </a>
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {processing
                ? "Guardando…"
                : method === "post"
                  ? "Guardar"
                  : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
